import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CopyQuestionModal } from "modules/QuestionsDashboard/components/EditQuestionModal/components/EditQuestionActions/components";
import { useDeleteQuestionMutation } from "modules/QuestionsDashboard/components/EditQuestionModal/components/EditQuestionActions/hooks";
import { isAdmin } from "utils/common";

import type { Question, QuestionID } from "models";

type EditQuestionActionsProps = {
  question: Question;
  canEdit: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onOpenQuestion: (questionID: QuestionID) => void;
};

export const EditQuestionActions = ({ question, canEdit, isSubmitting, onClose, onOpenQuestion }: EditQuestionActionsProps) => {
  const { t } = useTranslation();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { mutateAsync: deleteQuestion, isPending: isDeletePending } = useDeleteQuestionMutation();

  const canDeleteQuestions = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.Questions);

  const canDelete = canDeleteQuestions && (!!question.dealer_id || isAdmin(user?.role_id ?? 0));

  const handleDelete = async () => {
    await deleteQuestion({ id: question.id });
    setIsDeleteConfirmOpen(false);
    onClose();
  };

  const handleCopy = (questionID: QuestionID) => {
    setIsCopyModalOpen(false);
    onOpenQuestion(questionID);
  };

  return (
    <>
      <Modal.Actions>
        <div className="actions-left">
          {canDelete && (
            <Button type="button" color="red" onClick={() => setIsDeleteConfirmOpen(true)}>
              {t("v8_delete", "Delete")}
              <Icon className="trash" />
            </Button>
          )}

          <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Questions}>
            <Button type="button" color="orange" onClick={() => setIsCopyModalOpen(true)}>
              {t("v8_copy", "Copy")}
            </Button>
          </Can>
        </div>

        <div className="actions-right">
          <Button type="button" className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>

          {canEdit && (
            <Button type="submit" color="green" disabled={isSubmitting} loading={isSubmitting}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          )}
        </div>
      </Modal.Actions>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteConfirmOpen}
        isLoading={isDeletePending}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDelete}
        handleCancel={() => setIsDeleteConfirmOpen(false)}
      />

      <CopyQuestionModal isOpen={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onCopy={handleCopy} />
    </>
  );
};
